"use client";

import { useMemo, useState } from "react";
import { getCexLabel, getCexSignUpUrl } from "@cede/utils";
import { ExchangeIcon } from "@/components/icons";
import { Confirm } from "@/components/shared/Confirm";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { SkeletonElement } from "@/components/ui/skeleton";
import { useDevice, useHubUser, usePrioritizedItems } from "@/hooks";
import { cn } from "@/lib/utils";
import { ArrowUpRight, HelpCircle, LogOut } from "lucide-react";
import { useSnackbar } from "notistack";

interface LinkExchangesListProps {
  exchangeIds: string[];
  handleSelectCex: (exchangeId: string) => void;
  showRemoveButton?: boolean;
  showCampaignCounts?: boolean;
  campaignCountsByExchange?: Record<string, number>;
  sortByCampaigns?: boolean;
  isStepActive?: boolean;
  className?: string;
}

interface ExchangeButtonsProps {
  exchangeId: string;
  isLinked: boolean;
  showRemoveButton?: boolean;
  isMobile: boolean;
  isLoading: boolean;
  disabled?: boolean;
  disabledAddingButtons?: boolean;
  isRemoving?: boolean;
  onSignUp: (exchangeId: string) => void;
  onLink: (exchangeId: string) => void;
  onUnlink: (exchangeId: string) => void;
}

const CampaignCountTooltip = ({ campaignCount }: { campaignCount: number }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <span className="text-xs font-medium text-cede-primary/80">{`(${campaignCount})`}</span>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="text-cede-text-secondary transition-opacity hover:opacity-80"
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
          >
            <HelpCircle className="h-3.5 w-3.5" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-64 rounded-large border border-cede-border-container bg-cede-bg-popover text-left text-sm text-cede-text-primary"
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
        >
          Number of active trading campaigns available on this exchange.
        </PopoverContent>
      </Popover>
    </>
  );
};

const ExchangeButtons = ({
  exchangeId,
  isLinked,
  showRemoveButton = false,
  isMobile,
  isLoading,
  disabled,
  disabledAddingButtons,
  isRemoving,
  onSignUp,
  onLink,
  onUnlink,
}: ExchangeButtonsProps) => {
  if (!isLinked) {
    return (
      <div className="flex shrink-0 items-center justify-end gap-2">
        {!isMobile && (
          <Button
            variant="primary"
            size="small"
            onClick={() => onSignUp(exchangeId)}
            disabled={disabled || disabledAddingButtons}
          >
            Sign up
          </Button>
        )}
        <Button
          variant="secondary"
          size="small"
          onClick={() => onLink(exchangeId)}
          disabled={disabled || disabledAddingButtons}
          loading={isLoading}
          rightIcon={<ArrowUpRight className="h-4 w-4" />}
        >
          Link
        </Button>
      </div>
    );
  }

  if (!showRemoveButton) {
    return null;
  }

  return (
    <div className="flex shrink-0 items-center justify-end">
      <Button
        variant="unlink"
        size="small"
        onClick={() => onUnlink(exchangeId)}
        disabled={disabled}
        loading={isRemoving}
        leftIcon={<LogOut className="h-4 w-4" />}
      >
        {isRemoving ? "Unlinking..." : "Unlink"}
      </Button>
    </div>
  );
};

export const LinkExchangesList = ({
  exchangeIds,
  handleSelectCex,
  showRemoveButton = false,
  showCampaignCounts = false,
  campaignCountsByExchange = {},
  sortByCampaigns = false,
  className,
}: LinkExchangesListProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const { isMobile } = useDevice();
  const { hubUser, isLoadingHubUser, removeExchangeAccount, removeExchangeAccountIsLoading, userExchanges } =
    useHubUser();

  const [confirmExchangeId, setConfirmExchangeId] = useState<string | null>(null);
  const [isProcessingRemoval, setIsProcessingRemoval] = useState(false);

  const alreadyAddedExchangeIds = Object.keys(userExchanges);

  const prioritizedExchangeIds = usePrioritizedItems(exchangeIds, alreadyAddedExchangeIds);

  const sortedExchangeIds = useMemo(() => {
    if (!sortByCampaigns) {
      return prioritizedExchangeIds;
    }

    const connected = prioritizedExchangeIds.filter((id) => alreadyAddedExchangeIds.includes(id));
    const unconnected = prioritizedExchangeIds
      .filter((id) => !alreadyAddedExchangeIds.includes(id))
      .sort((a, b) => {
        const countA = campaignCountsByExchange[a] ?? 0;
        const countB = campaignCountsByExchange[b] ?? 0;
        return countB - countA;
      });

    return [...connected, ...unconnected];
  }, [prioritizedExchangeIds, sortByCampaigns, campaignCountsByExchange, alreadyAddedExchangeIds]);

  const handleSignUp = (cex: string) => {
    const url = getCexSignUpUrl(cex);
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  const handleRemove = async (exchangeId: string) => {
    const account = userExchanges[exchangeId];
    if (!account) {
      enqueueSnackbar("Failed to remove exchange account", { variant: "error" });
      return;
    }

    setIsProcessingRemoval(true);
    try {
      await removeExchangeAccount({ accountId: account.id.toString(), exchangeId });
    } finally {
      setIsProcessingRemoval(false);
      setConfirmExchangeId(null);
    }
  };

  const isLoading = !hubUser || isLoadingHubUser;

  return (
    <>
      <div className={cn("flex flex-col divide-y divide-cede-border-container/70", className)}>
        {isLoading && sortedExchangeIds.length === 0
          ? Array.from({ length: 4 }).map((_, index) => (
              <SkeletonElement key={index} className="h-16 w-full rounded-xl" />
            ))
          : sortedExchangeIds.map((exchangeId) => {
              const isLinked = alreadyAddedExchangeIds.includes(exchangeId);
              const campaignCount = campaignCountsByExchange[exchangeId] ?? 0;
              const label = getCexLabel(exchangeId);

              return (
                <div
                  key={exchangeId}
                  className={cn("flex w-full flex-row items-center justify-between gap-3 py-3 md:py-4 opacity-100")}
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <ExchangeIcon exchange={exchangeId} size="md" withUnderlay />
                    <div className="flex min-w-0 flex-col">
                      <div className="flex items-center gap-1">
                        <span className="truncate text-base font-medium text-cede-text-primary">{label}</span>
                        {showCampaignCounts && campaignCount > 0 && (
                          <CampaignCountTooltip campaignCount={campaignCount} />
                        )}
                      </div>
                      {isLinked && (
                        <div className="flex items-center h-3">
                          <div className="text-badge text-cede-primary">Linked</div>
                        </div>
                      )}
                    </div>
                  </div>

                  <ExchangeButtons
                    exchangeId={exchangeId}
                    isLinked={isLinked}
                    showRemoveButton={showRemoveButton}
                    isMobile={isMobile}
                    isLoading={isLoading}
                    disabled={removeExchangeAccountIsLoading || isProcessingRemoval}
                    disabledAddingButtons={!!hubUser?.isBanned}
                    isRemoving={isProcessingRemoval && confirmExchangeId === exchangeId}
                    onSignUp={handleSignUp}
                    onLink={handleSelectCex}
                    onUnlink={(id) => setConfirmExchangeId(id)}
                  />
                </div>
              );
            })}
      </div>

      <Confirm
        open={!!confirmExchangeId}
        title={`Unlink ${confirmExchangeId ? getCexLabel(confirmExchangeId) : "exchange"}`}
        description="Are you sure you want to unlink this CEX from the wallet?"
        onConfirm={async () => {
          if (confirmExchangeId) {
            await handleRemove(confirmExchangeId);
          }
        }}
        onClose={() => {
          setConfirmExchangeId(null);
          setIsProcessingRemoval(false);
        }}
      />
    </>
  );
};
